import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText, ShieldCheck, DollarSign } from "lucide-react";

/**
 * Dialog completo de cadastro/edição de contrato.
 * Recebe form + setForm do parent (controlled) e delega submit ao parent.
 */
export function ContratoFormDialog({ open, onOpenChange, mode, form, setForm, unidadesLivres, onSave }) {
  const isPF = form.tipo_pessoa === "PF";

  const updField = (key, value) => setForm({ ...form, [key]: value });
  const updNested = (parent, key, value) => setForm({ ...form, [parent]: { ...form[parent], [key]: value } });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-sm">
        <DialogHeader>
          <DialogTitle>{mode === "editar" ? "Editar Contrato" : "Novo Contrato"}</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="dados" className="max-h-[70vh] overflow-y-auto">
          <TabsList className="rounded-sm">
            <TabsTrigger value="dados" data-testid="tab-dados"><FileText size={12} className="mr-1" />Dados</TabsTrigger>
            <TabsTrigger value="fiador" data-testid="tab-fiador"><ShieldCheck size={12} className="mr-1" />Fiador</TabsTrigger>
            <TabsTrigger value="caucao" data-testid="tab-caucao"><DollarSign size={12} className="mr-1" />Caução</TabsTrigger>
            <TabsTrigger value="unidades" data-testid="tab-unidades">Unidades</TabsTrigger>
          </TabsList>

          <TabsContent value="dados" className="mt-4 space-y-3">
            <div>
              <Label className="label-tag">Tipo de pessoa</Label>
              <div className="flex gap-2">
                <button type="button" data-testid="pj-toggle" onClick={() => updField("tipo_pessoa", "PJ")}
                  className={`flex-1 py-3 border rounded-sm text-sm font-semibold ${form.tipo_pessoa === "PJ" ? "bg-foreground text-background border-foreground" : "border-border"}`}>
                  Pessoa Jurídica (CNPJ)
                </button>
                <button type="button" data-testid="pf-toggle" onClick={() => updField("tipo_pessoa", "PF")}
                  className={`flex-1 py-3 border rounded-sm text-sm font-semibold ${form.tipo_pessoa === "PF" ? "bg-foreground text-background border-foreground" : "border-border"}`}>
                  Pessoa Física (CPF)
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-1 sm:col-span-2">
                <Label className="label-tag">{isPF ? "Nome completo" : "Razão Social"}</Label>
                <Input data-testid="cnew-razao" value={form.razao_social} onChange={(e) => updField("razao_social", e.target.value)} />
              </div>
              <div>
                <Label className="label-tag">{isPF ? "CPF" : "CNPJ"}</Label>
                <Input data-testid="cnew-cnpj" value={form.cnpj} onChange={(e) => updField("cnpj", e.target.value)} placeholder={isPF ? "000.000.000-00" : "00.000.000/0001-00"} />
              </div>
              <div>
                <Label className="label-tag">Telefone (WhatsApp)</Label>
                <Input data-testid="cnew-tel" value={form.telefone} onChange={(e) => updField("telefone", e.target.value)} placeholder="+55..." />
              </div>
              <div>
                <Label className="label-tag">Responsável</Label>
                <Input data-testid="cnew-resp" value={form.responsavel} onChange={(e) => updField("responsavel", e.target.value)} />
              </div>
              <div>
                <Label className="label-tag">E-mail</Label>
                <Input data-testid="cnew-email" type="email" value={form.email} onChange={(e) => updField("email", e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label className="label-tag">Endereço completo (para o boleto)</Label>
                <Input data-testid="cnew-endereco" value={form.endereco} onChange={(e) => updField("endereco", e.target.value)} placeholder="Rua, número, bairro" />
              </div>
              <div>
                <Label className="label-tag">CEP</Label>
                <Input data-testid="cnew-cep" value={form.cep} onChange={(e) => updField("cep", e.target.value)} placeholder="00000-000" maxLength={9} />
              </div>
              <div>
                <Label className="label-tag">Cidade</Label>
                <Input data-testid="cnew-cidade" value={form.cidade} onChange={(e) => updField("cidade", e.target.value)} placeholder="Ex: Magé" />
              </div>
              <div>
                <Label className="label-tag">UF (2 letras)</Label>
                <Input data-testid="cnew-uf" value={form.estado} onChange={(e) => updField("estado", e.target.value.toUpperCase().slice(0, 2))} placeholder="RJ" maxLength={2} />
              </div>
              <div>
                <Label className="label-tag">Aluguel base da loja (R$)</Label>
                <Input data-testid="cnew-valor" type="number" step="0.01" value={form.valor_aluguel} onChange={(e) => updField("valor_aluguel", e.target.value)} placeholder="Valor fixo do aluguel" />
              </div>
              <div>
                <Label className="label-tag">Dia vencimento</Label>
                <Input data-testid="cnew-venc" type="number" min="1" max="28" value={form.dia_vencimento} onChange={(e) => updField("dia_vencimento", e.target.value)} />
              </div>
              <div>
                <Label className="label-tag">Multa por atraso (%)</Label>
                <Input data-testid="cnew-multa" type="number" step="0.01" value={form.multa_atraso_percent} onChange={(e) => updField("multa_atraso_percent", e.target.value)} placeholder="2.00" />
              </div>
              <div>
                <Label className="label-tag">Mora / juros diários (%)</Label>
                <Input data-testid="cnew-mora" type="number" step="0.001" value={form.mora_diaria_percent} onChange={(e) => updField("mora_diaria_percent", e.target.value)} placeholder="0.033" />
              </div>
              <div>
                <Label className="label-tag">Taxa de Água Comercial (R$/mês)</Label>
                <Input data-testid="cnew-agua" type="number" step="0.01" value={form.taxa_agua_mensal} onChange={(e) => updField("taxa_agua_mensal", e.target.value)} placeholder="0,00" />
              </div>
              <div>
                <Label className="label-tag">Taxa de Rateio Contratual (R$/mês)</Label>
                <Input data-testid="cnew-rateio-fixa" type="number" step="0.01" value={form.taxa_rateio_fixa} onChange={(e) => updField("taxa_rateio_fixa", e.target.value)} placeholder="0,00 (usa rateio dinâmico)" />
                <div className="text-[10px] text-muted-foreground mt-1">
                  Se {'>'} 0, sobrescreve o rateio dinâmico do mês para este contrato.
                </div>
              </div>
              <div>
                <Label className="label-tag">Data Início</Label>
                <Input data-testid="cnew-inicio" type="date" value={form.data_inicio} onChange={(e) => updField("data_inicio", e.target.value)} />
              </div>
              <div>
                <Label className="label-tag">Data Fim</Label>
                <Input data-testid="cnew-fim" type="date" value={form.data_fim} onChange={(e) => updField("data_fim", e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label className="label-tag">Índice de reajuste</Label>
                <Select value={form.indice_reajuste} onValueChange={(v) => updField("indice_reajuste", v)}>
                  <SelectTrigger data-testid="cnew-indice"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IPCA">IPCA</SelectItem>
                    <SelectItem value="IGP-M">IGP-M</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="fiador" className="mt-4 space-y-3">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Preencha os dados do fiador. Deixe em branco caso não haja fiador (haverá então apenas caução).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-1 sm:col-span-2">
                <Label className="label-tag">Nome do fiador</Label>
                <Input data-testid="fiador-nome" value={form.fiador.nome} onChange={(e) => updNested("fiador", "nome", e.target.value)} />
              </div>
              <div>
                <Label className="label-tag">CPF/CNPJ</Label>
                <Input data-testid="fiador-doc" value={form.fiador.documento} onChange={(e) => updNested("fiador", "documento", e.target.value)} />
              </div>
              <div>
                <Label className="label-tag">Telefone</Label>
                <Input data-testid="fiador-tel" value={form.fiador.telefone} onChange={(e) => updNested("fiador", "telefone", e.target.value)} />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <Label className="label-tag">E-mail</Label>
                <Input data-testid="fiador-email" value={form.fiador.email} onChange={(e) => updNested("fiador", "email", e.target.value)} />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <Label className="label-tag">Endereço completo</Label>
                <Textarea data-testid="fiador-end" value={form.fiador.endereco} onChange={(e) => updNested("fiador", "endereco", e.target.value)} rows={2} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="caucao" className="mt-4 space-y-3">
            <p className="text-xs text-muted-foreground leading-relaxed">Registre o depósito caução recebido, se houver.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="label-tag">Valor depositado (R$)</Label>
                <Input data-testid="caucao-valor" type="number" step="0.01" value={form.caucao.valor} onChange={(e) => updNested("caucao", "valor", e.target.value)} />
              </div>
              <div>
                <Label className="label-tag">Data do depósito</Label>
                <Input data-testid="caucao-data" type="date" value={form.caucao.data_deposito} onChange={(e) => updNested("caucao", "data_deposito", e.target.value)} />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <Label className="label-tag">Banco / Conta</Label>
                <Input data-testid="caucao-banco" value={form.caucao.banco} onChange={(e) => updNested("caucao", "banco", e.target.value)} />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <Label className="label-tag">Observações</Label>
                <Textarea data-testid="caucao-obs" value={form.caucao.observacoes} onChange={(e) => updNested("caucao", "observacoes", e.target.value)} rows={2} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="unidades" className="mt-4">
            <Label className="label-tag">Selecione as unidades ({form.unidades_ids.length} marcadas)</Label>
            <div className="max-h-72 overflow-y-auto border border-border rounded-sm p-3 grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {unidadesLivres
                .filter((u) => u.status === "LIVRE" || form.unidades_ids.includes(u.id))
                .map((u) => {
                  const selected = form.unidades_ids.includes(u.id);
                  const jaOcupadaPorOutro = u.status === "OCUPADA" && !selected;
                  return (
                    <button
                      type="button"
                      key={u.id}
                      disabled={jaOcupadaPorOutro}
                      data-testid={`cnew-un-${u.numero_completo}`}
                      onClick={() =>
                        updField("unidades_ids",
                          selected ? form.unidades_ids.filter((x) => x !== u.id) : [...form.unidades_ids, u.id]
                        )
                      }
                      className={`text-xs p-2 rounded-sm border transition-colors ${
                        selected ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"
                      } ${jaOcupadaPorOutro ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      {u.numero_completo}
                    </button>
                  );
                })}
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="cnew-salvar" onClick={onSave} className="bg-foreground text-background hover:bg-foreground/90">
            {mode === "editar" ? "Salvar alterações" : "Criar contrato"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
